import { useState, useEffect, useCallback } from "react";
import { Cpu, Loader2, CheckCircle, Sparkles, AlertTriangle, ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppLayout from "@/components/AppLayout";
import { uid, COMPANY_TYPES } from "./technology/constants";
import ProductTab from "./technology/ProductTab";
import DefensibilityTab from "./technology/DefensibilityTab";
import ValueCaptureTab from "./technology/ValueCaptureTab";
import StrategyTabBar from "./StrategyTabBar";
import ClusteredTodoTab from "./ClusteredTodoTab";
import { useStrategyModule } from "@/hooks/useStrategyModule";

const VALID_TABS = new Set(["product", "defensibility", "value_capture", "todos"]);
const SUB_TOPICS = [
  { key: "product",       label: "Technology" },
  { key: "defensibility", label: "Defensibility" },
  { key: "value_capture", label: "Business Model" },
];
const HEADERS = {
  product:       { title: "Technology",       subtitle: "What are we actually building? — components, platform claim and near-term value." },
  defensibility: { title: "Defensibility",    subtitle: "What do we own, control or need to protect? — IP map and safe-disclosure boundaries." },
  value_capture: { title: "Business Model",   subtitle: "How does this become a business? — routes, models, evidence and who pays for what." },
  todos:         { title: "To Do",            subtitle: "Your top 5 priorities across the three topics. Generate a Technology, IP & Business Model overview when you're ready." },
};

const EMPTY_PROD = { primary_output: "", current_lead_programme: "", platform_claim: "", diligence_focus: "", company_type: "", component_map: [], near_term_value: "", platform_proof: "", reusable: "", asset_specific: "", external_focus: "", key_questions: [], guidance: [] };
const EMPTY_DEF = { ip_map: [], scores: [], safe_disclose: "", confidential: "", needs_filing: "", collab_ownership: "", expert_questions: "", key_questions: [], guidance: [] };
const EMPTY_VC = { primary_route: "", primary_model: "", secondary_model: "", who_pays: "", what_pays_for: "", when_pay: "", evidence_needed: "", our_costs: "", partner_costs: "", monetisable_unit: "", pay_first: "", pay_more: "", pay_wait: "", pay_walk: "", strongest_hypothesis: "", biggest_contradiction: "", revenue_map: [], key_questions: [], guidance: [] };

export default function TechnologyStrategy() {
  const shell = useStrategyModule({ endpointKey: "technology", defaultTab: "product", validTabs: VALID_TABS });
  const { user, companyProfile, loading, saving, activeTab, setActiveTab, urlTab, followups, setFollowups, card, setCard, cardOpen, setCardOpen, generatingCard, fetchProgress, postProgress, postGenerateCard } = shell;

  const [prod, setProd] = useState(EMPTY_PROD);
  const [def, setDef] = useState(EMPTY_DEF);
  const [vc, setVc] = useState(EMPTY_VC);

  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const d = await fetchProgress();
    if (!d) return;
    if (d.product) setProd({ ...EMPTY_PROD, ...d.product });
    if (d.defensibility) setDef({ ...EMPTY_DEF, ...d.defensibility });
    if (d.value_capture) setVc({ ...EMPTY_VC, ...d.value_capture });
    setFollowups((d.followups || []).map((f) => ({ id: f.id || uid(), text: f.text || "", done: !!f.done, sub_module: f.sub_module || null })));
    if (d.card) setCard(d.card);
    if (d.activeView && VALID_TABS.has(d.activeView) && !urlTab) setActiveTab(d.activeView);
  };

  const persist = useCallback(async (overrides = {}, showToast = false) => {
    const payload = { product: prod, defensibility: def, value_capture: vc, followups, card, activeView: activeTab, ...overrides };
    await postProgress(payload, showToast);
  }, [prod, def, vc, followups, card, activeTab, postProgress]);

  const generateCard = async () => {
    const c = await postGenerateCard({ progress: { product: prod, defensibility: def, value_capture: vc } });
    if (c) persist({ card: c });
  };

  if (loading) return <AppLayout user={user}><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div></AppLayout>;

  const companyTypeLabel = card?.company_type || COMPANY_TYPES.find((t) => t.id === prod.company_type)?.label || "";

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="technology-strategy-page">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Cpu className="w-5 h-5 text-teal-600" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{HEADERS[activeTab]?.title || "Technology, IP & Business Model"}</h1>
              <p className="text-sm text-slate-500">{HEADERS[activeTab]?.subtitle}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => persist({}, true)} data-testid="tech-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Save</Button>
        </div>

        <div data-testid={`tech-panel-${activeTab}`} className="space-y-8">
          <StrategyTabBar
            tabs={SUB_TOPICS}
            activeTab={activeTab}
            onSelect={setActiveTab}
            todoCount={followups.filter((f) => !f.done).length}
          />

          {activeTab === "product"       && <ProductTab       data={prod} setData={setProd} persist={persist} />}
          {activeTab === "defensibility" && <DefensibilityTab data={def}  setData={setDef}  persist={persist} productData={prod} />}
          {activeTab === "value_capture" && <ValueCaptureTab  data={vc}   setData={setVc}   persist={persist} />}
          {activeTab === "todos" && (
            <ClusteredTodoTab
              items={followups}
              setItems={setFollowups}
              persist={persist}
              endpointKey="technology"
              getSuggestBody={(existing) => ({
                progress: { product: prod, defensibility: def, value_capture: vc },
                existing,
              })}
              onGenerateCard={generateCard}
              generatingCard={generatingCard}
              hasCard={!!card}
              onOpenCard={() => setCardOpen(true)}
              groupTitle="Generate Technology, IP & Business Model overview"
            />
          )}
        </div>
      </div>

      <Dialog open={cardOpen} onOpenChange={(o) => { if (!o) setCardOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tech-card-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Technology–IP–Business Model Strategy Card</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => window.print()}><Download className="w-4 h-4 mr-2" /> Print / PDF</Button>
            </div>
            <DialogDescription className="sr-only">Generated Technology / IP / Business Model card.</DialogDescription>
          </DialogHeader>
          {!card ? <div className="py-8 text-center text-slate-500">No card yet.</div> : (
            <div className="space-y-4 text-sm">
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <h3 className="text-lg font-bold text-teal-900">{companyProfile?.company_name}</h3>
                {companyTypeLabel && <Badge className="mt-1 mr-2 bg-teal-100 text-teal-800 border-teal-300" variant="outline">{companyTypeLabel}</Badge>}
                <p className="text-teal-700 mt-2">{card.what_we_are_building}</p>
              </div>
              {card.core_value_components?.length > 0 && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Core value components</h4><div className="flex flex-wrap gap-1">{card.core_value_components.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}</div></CardContent></Card>}
              {card.what_we_own_or_control && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">What we own or control</h4><p className="text-slate-600">{card.what_we_own_or_control}</p></CardContent></Card>}
              {card.weakly_protected && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Weakly protected / partner-dependent</h4><p className="text-slate-600">{card.weakly_protected}</p></CardContent></Card>}
              {card.main_defensibility_gap && <div className="border rounded-lg p-4 border-amber-200 bg-amber-50"><h4 className="text-sm font-medium text-amber-900 mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Main defensibility gap</h4><p className="text-amber-800">{card.main_defensibility_gap}</p></div>}
              {card.primary_value_capture_route && <Card><CardContent className="pt-4"><h4 className="text-sm font-medium text-slate-700 mb-1">Primary value-capture route</h4><p className="text-slate-600">{card.primary_value_capture_route}</p></CardContent></Card>}
              {card.evidence_needed?.length > 0 && <div className="border rounded-lg p-4"><h4 className="text-sm font-medium text-slate-700 mb-2">Evidence needed to unlock that route</h4><ul className="space-y-1">{card.evidence_needed.map((r, i) => <li key={i} className="text-slate-600">• {r}</li>)}</ul></div>}
              {card.biggest_contradiction && <div className="border rounded-lg p-4 border-red-200 bg-red-50"><h4 className="text-sm font-medium text-red-900 mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Biggest contradiction</h4><p className="text-red-800 italic">"{card.biggest_contradiction}"</p></div>}
              {card.top_expert_questions?.length > 0 && (<div className="border rounded-lg p-4 bg-teal-50 border-teal-200"><h4 className="text-sm font-medium text-teal-900 mb-2 flex items-center gap-2"><ArrowRight className="w-4 h-4" /> Top questions for experts</h4><ul className="space-y-1">{card.top_expert_questions.map((q, i) => <li key={i} className="text-teal-800">• {q}</li>)}</ul></div>)}
              <div className="flex justify-end pt-2 border-t"><Button onClick={generateCard} disabled={generatingCard}>{generatingCard ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Regenerate</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
